import { useState } from "react";
import { ToggleButton } from "@/components/ui";
import { RangeSlider } from "@/components/ui/RangeSlider";
import { TimeInput } from "@/components/ui/TimeInput";
import { AddressAutocomplete, geocodePickedAddress } from "./AddressAutocomplete";
import { AutofillBadge } from "./AutofillBadge";
import { CheckboxCard } from "./CheckboxCard";
import { FormField } from "./FormField";
import { StepFooter } from "./StepFooter";
import { computeAttendanceChange, validateVenueStep } from "./validators";
import { attendanceBands } from "./types";
import {
  ATTENDANCE_MAX,
  ATTENDANCE_MIN,
  ATTENDANCE_REST,
  POSITIONS,
  attendanceAt,
  positionOf,
} from "./attendance-scale";
import type { StepProps } from "./step-props";
import { FieldError } from "@/components/ui/FieldError";
import step from "./step.module.css";
import styles from "./VenueStep.module.css";

export function VenueStep({
  profile,
  formData,
  activeFields,
  showValidation,
  autofilledFields,
  invalidFields,
  setFieldValidity,
  updateField,
  onBack,
  onContinue,
  continueLabel,
  onShowValidation,
}: StepProps & { autofilledFields?: Set<string> }): React.JSX.Element {
  const copy = profile.venueStep;
  // A Company may not offer a site visit (migration 053); absent is offered.
  const siteVisitOffered = profile.siteVisit?.offered !== false;

  // One-way disclosure: opens when the user asks for it, or on mount when a
  // draft/restore already carries an exact number.
  const [showExactAttendance, setShowExactAttendance] = useState(
    formData.attendance !== undefined,
  );
  // Same one-way disclosure for the optional timing/venue-contact fields —
  // most customers skip them; a restored draft with values re-opens it.
  const [showTiming, setShowTiming] = useState(
    Boolean(
      formData.siteAvailableFrom || formData.packOutTime || formData.venueContact,
    ),
  );

  const handleScaleChange = (raw: string): void => {
    const result = computeAttendanceChange(raw);
    if (result.kind === "clear") {
      // Empty field → undefined (NOT 0), so the validator distinguishes an
      // unanswered field from a malformed entry.
      updateField(copy.scale.field, undefined);
      return;
    }
    if (result.kind === "invalid") {
      // Non-empty but out of range — keep the number so it reads as
      // "invalid" rather than unanswered.
      updateField(copy.scale.field, result.count);
      return;
    }
    updateField(copy.scale.field, result.count);
  };

  // Location text + pick-time coordinates. Typing invalidates any previous
  // pick's coords (they belong to the exact picked string, nothing else);
  // picking a suggestion geocodes it so the inquiry carries verified
  // coordinates. Geocode failure just means no coords — never a blocker.
  const handleLocationChange = (v: string): void => {
    updateField(copy.fields.location.field, v);
    if (formData.siteGeocodedLocation && v !== formData.siteGeocodedLocation) {
      updateField("siteLat", undefined);
      updateField("siteLng", undefined);
      updateField("siteGeocodedLocation", undefined);
    }
  };
  const handleLocationPick = (picked: string): void => {
    void geocodePickedAddress(picked).then((res) => {
      if (!res) return;
      updateField("siteLat", res.lat);
      updateField("siteLng", res.lng);
      updateField("siteGeocodedLocation", res.address);
    });
  };

  const errors = validateVenueStep(formData, profile);
  const showOutdoorFields = activeFields
    ? activeFields.has(copy.outdoor.powerAccess.field)
    : formData[copy.outdoor.when.field] === copy.outdoor.when.equals;

  const handleContinue = (): void => {
    // invalidFields gates Continue only for inputs that swallow unparseable
    // text. Time fields keep the raw text in formData, so the validator map
    // now covers them — but the gate stays harmless.
    const noInvalid = !invalidFields || invalidFields.size === 0;
    if (Object.keys(errors).length === 0 && noInvalid) {
      onContinue();
    } else {
      onShowValidation([
        ...new Set([...Object.keys(errors), ...(invalidFields ?? [])]),
      ]);
    }
  };

  const indoorOutdoorError = showValidation ? errors.indoorOutdoor : undefined;
  const attendanceError = showValidation ? errors.attendance : undefined;
  const locationError = showValidation ? errors.location : undefined;

  // The slider is the primary control: dragging writes an exact attendance
  // number (and derives the package tier via handleScaleChange). "Not sure
  // yet" is the escape hatch (attendanceBand "unsure", no number); the
  // exact-number disclosure is the precision path for >5,000 or specifics.
  // The track is logarithmic (attendance-scale.ts); nothing is written
  // until the customer drags.
  const isUnsure = formData.attendanceBand === "unsure";
  const sliderPosition = positionOf(formData.attendance ?? ATTENDANCE_REST);

  const bandLabel = attendanceBands.find(
    ({ value }) => value === formData.attendanceBand,
  )?.label;
  const attendanceReadout =
    formData.attendance !== undefined
      ? `${formData.attendance} guests`
      : (bandLabel ?? "Drag to set");
  const attendanceAnswered =
    formData.attendance !== undefined || formData.attendanceBand !== undefined;

  const handleSliderChange = (position: number): void => {
    if (formData.attendanceBand) {
      updateField("attendanceBand", undefined);
    }
    handleScaleChange(String(attendanceAt(position)));
  };

  const handleUnsureToggle = (): void => {
    if (isUnsure) {
      updateField("attendanceBand", undefined);
      return;
    }
    updateField("attendanceBand", "unsure");
    updateField(copy.scale.field, undefined);
  };

  const handleExactChange = (raw: string): void => {
    if (raw.trim() !== "" && formData.attendanceBand) {
      updateField("attendanceBand", undefined);
    }
    handleScaleChange(raw);
  };

  return (
    <div className={step.step}>
      <div>
        <h1 className={step.heading}>{copy.title}</h1>
        <div className={styles.fields}>
          <div>
            <label
              htmlFor="inquiry-field-location"
              className={styles.label}
            >
              {copy.fields.location.label}
              <AutofillBadge field="location" autofilledFields={autofilledFields} />
            </label>
            <AddressAutocomplete
              id="inquiry-field-location"
              value={formData.location ?? ""}
              onChange={handleLocationChange}
              onPick={handleLocationPick}
            />
            <FieldError message={locationError} />
          </div>

          {siteVisitOffered && (
            <CheckboxCard
              label={copy.siteVisit.available.label}
              description={copy.siteVisit.available.description}
              checked={formData.siteVisit || false}
              onChange={(v) => updateField(copy.siteVisit.available.field, v)}
            >
              {formData.siteVisit &&
                activeFields?.has(copy.siteVisit.when.field) !== false && (
                  <div className={styles.nested}>
                    <FormField
                      label={copy.siteVisit.when.label}
                      field={copy.siteVisit.when.field}
                      formData={formData}
                      optional
                      hint={copy.siteVisit.when.hint}
                      onChange={(v) => updateField(copy.siteVisit.when.field, v)}
                    />
                  </div>
                )}
            </CheckboxCard>
          )}

          <div>
            <label className={styles.label}>
              {copy.fields.environment.label}
              <AutofillBadge field="indoorOutdoor" autofilledFields={autofilledFields} />
            </label>
            <div
              className={styles.environment}
              role="radiogroup"
              aria-label="Environment"
              aria-invalid={indoorOutdoorError ? true : undefined}
            >
              {copy.fields.environment.options.map((opt) => (
                <ToggleButton
                  key={opt}
                  pressed={formData.indoorOutdoor === opt}
                  onPress={() => updateField("indoorOutdoor", opt)}
                  className={
                    indoorOutdoorError && formData.indoorOutdoor !== opt
                      ? styles.choiceInvalid
                      : undefined
                  }
                >
                  {opt}
                </ToggleButton>
              ))}
            </div>
            <FieldError message={indoorOutdoorError} />
          </div>

          {showOutdoorFields && (
            <>
              <FormField
                label={copy.outdoor.powerAccess.label}
                field={copy.outdoor.powerAccess.field}
                formData={formData}
                required
                showValidation={showValidation}
                error={errors.powerAccess}
                onChange={(v) => updateField(copy.outdoor.powerAccess.field, v)}
              />
              <FormField
                as="textarea"
                label={copy.outdoor.wetWeatherPlan.label}
                field={copy.outdoor.wetWeatherPlan.field}
                formData={formData}
                rows={10}
                onChange={(v) => updateField(copy.outdoor.wetWeatherPlan.field, v)}
              />
            </>
          )}

          <div>
            <div className={styles.sliderHeader}>
              <label className={styles.sliderLabel}>
                {copy.scale.label}
                <AutofillBadge field="attendance" autofilledFields={autofilledFields} />
              </label>
              {/* When the exact-number input is open it IS the readout —
                  showing both prints the same answer twice. */}
              {!showExactAttendance && (
                <span
                  className={`${styles.readout} ${
                    attendanceAnswered ? styles.readoutAnswered : ""
                  }`}
                >
                  {attendanceReadout}
                </span>
              )}
            </div>
            <RangeSlider
              min={0}
              max={POSITIONS}
              step={1}
              value={sliderPosition}
              onChange={handleSliderChange}
              aria-label={copy.scale.label}
            />
            <div className={styles.sliderBounds}>
              <span>{ATTENDANCE_MIN}</span>
              <span>{ATTENDANCE_MAX.toLocaleString("en-NZ")}+</span>
            </div>
            {!showExactAttendance && <FieldError message={attendanceError} />}
            <div className={styles.sliderActions}>
              <button
                type="button"
                aria-pressed={isUnsure}
                onClick={handleUnsureToggle}
                className={`${styles.textButton} ${
                  isUnsure ? styles.textButtonPressed : ""
                }`}
              >
                Not sure yet
              </button>
              {!showExactAttendance && (
                <button
                  type="button"
                  onClick={() => setShowExactAttendance(true)}
                  className={styles.textButton}
                >
                  Type an exact number
                </button>
              )}
            </div>
            {showExactAttendance && (
              <div className={styles.nestedWide}>
                <FormField
                  label="Exact number"
                  field={copy.scale.field}
                  formData={formData}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  optional={Boolean(formData.attendanceBand)}
                  showValidation={showValidation}
                  error={errors.attendance}
                  onChange={handleExactChange}
                />
              </div>
            )}
          </div>

          <CheckboxCard
            label={copy.stage.available.label}
            description={copy.stage.available.description}
            checked={formData.hasStage || false}
            onChange={(v) => updateField(copy.stage.available.field, v)}
          >
            {formData.hasStage &&
              activeFields?.has(copy.stage.details.field) !== false && (
                <div className={styles.nested}>
                  <FormField
                    as="textarea"
                    label={copy.stage.details.label}
                    field={copy.stage.details.field}
                    formData={formData}
                    rows={1}
                    onChange={(v) => updateField(copy.stage.details.field, v)}
                  />
                </div>
              )}
          </CheckboxCard>

          <div className={styles.timing}>
            {!showTiming ? (
              <button
                type="button"
                onClick={() => setShowTiming(true)}
                className={styles.textButton}
              >
                Add timing &amp; venue details
              </button>
            ) : (
            <>
            <div className={styles.pair}>
              <div>
                <label className={styles.label}>
                  {copy.timing.siteAvailableFrom.label}
                </label>
                <TimeInput
                  value={formData[copy.timing.siteAvailableFrom.field] || ""}
                  onChange={(val) => updateField(copy.timing.siteAvailableFrom.field, val)}
                  error={showValidation ? errors.siteAvailableFrom : undefined}
                  onValidityChange={
                    setFieldValidity
                      ? (valid) => setFieldValidity("siteAvailableFrom", valid)
                      : undefined
                  }
                />
                {copy.timing.siteAvailableFrom.hint && (
                  <p className={styles.hint}>
                    {copy.timing.siteAvailableFrom.hint}
                  </p>
                )}
              </div>
              <div>
                <label className={styles.label}>
                  {copy.timing.packOutTime.label}
                </label>
                <TimeInput
                  value={formData[copy.timing.packOutTime.field] || ""}
                  onChange={(val) => updateField(copy.timing.packOutTime.field, val)}
                  error={showValidation ? errors.packOutTime : undefined}
                  onValidityChange={
                    setFieldValidity
                      ? (valid) => setFieldValidity("packOutTime", valid)
                      : undefined
                  }
                />
                {copy.timing.packOutTime.hint && (
                  <p className={styles.hint}>
                    {copy.timing.packOutTime.hint}
                  </p>
                )}
              </div>
            </div>
            <div className={styles.nestedWide}>
              <FormField
                label={copy.fields.venueContact.label}
                field={copy.fields.venueContact.field}
                formData={formData}
                optional={copy.fields.venueContact.optional}
                onChange={(v) => updateField(copy.fields.venueContact.field, v)}
              />
            </div>
            </>
            )}
          </div>
        </div>
      </div>

      <StepFooter
        onBack={onBack}
        onContinue={handleContinue}
        continueLabel={continueLabel}
      />
    </div>
  );
}
